/**
 * Two-tier free shipping progress bar.
 *
 * The theme's built-in freeShippingBar function only supports a single tier
 * (free shipping above a threshold). Bluesky has two:
 *   - Tier 1: free standard shipping  (default £15)
 *   - Tier 2: free next-business-day   (default £50)
 *
 * This script takes over when `snippets/free-shipping-bar.liquid` renders the
 * bar with the `data-two-tier-shipping-bar` attribute (instead of the theme's
 * `data-free-shipping-bar`). The theme's own freeShippingBar function skips
 * this element entirely, so we own the message and progress bar.
 *
 * AOV logic: when a customer hits tier 1 the bar visually RESETS and starts
 * filling toward tier 2 — the win-then-new-goal dopamine pattern that lifts
 * basket size. Confirmed to be the cleanest UX approach.
 */
(function () {
  'use strict';

  var SELECTOR = '[data-two-tier-shipping-bar]';
  var BAR_CLASS = 'free-shipping-bar__bar';
  var MESSAGE_CLASS = 'free-shipping-bar__message';
  var OVER_THRESHOLD_ATTR = 'data-is-over-threshold';

  // Track our own updates so we don't recurse when the DOM mutation observer
  // (watching cart section refreshes) fires.
  var updating = false;

  function formatMoney(cents) {
    // Match Shopify's default GBP formatting. If the theme's Shopify.formatMoney
    // global is available, prefer it for full locale/currency accuracy.
    if (window.Shopify && typeof window.Shopify.formatMoney === 'function') {
      try {
        return window.Shopify.formatMoney(cents, window.theme && window.theme.moneyFormat);
      } catch (e) {
        /* fall through to manual */
      }
    }
    var pounds = (cents / 100).toFixed(2);
    return '£' + pounds;
  }

  function setMessageText(messageEl, text, boldAmount) {
    // Use node-level operations to avoid innerHTML with any dynamic values.
    while (messageEl.firstChild) messageEl.removeChild(messageEl.firstChild);
    if (boldAmount) {
      // Text can contain a `{{AMOUNT}}` placeholder for the bold money value.
      var parts = text.split('{{AMOUNT}}');
      messageEl.appendChild(document.createTextNode(parts[0] || ''));
      var strong = document.createElement('span');
      strong.className = 'fs-body-bold';
      strong.appendChild(document.createTextNode(boldAmount));
      messageEl.appendChild(strong);
      if (parts.length > 1) {
        messageEl.appendChild(document.createTextNode(parts[1] || ''));
      }
    } else {
      messageEl.appendChild(document.createTextNode(text));
    }
  }

  function render(bar) {
    if (!bar) return;
    updating = true;

    var messageEl = bar.querySelector('.' + MESSAGE_CLASS);
    var progressEl = bar.querySelector('.' + BAR_CLASS);
    if (!messageEl || !progressEl) {
      updating = false;
      return;
    }

    // Thresholds are stored as pounds (e.g. "15.00"), convert to pence.
    var tier1 = parseFloat(bar.getAttribute('data-tier1-threshold')) || 0;
    var tier2 = parseFloat(bar.getAttribute('data-tier2-threshold')) || 0;
    var tier1Cents = Math.round(tier1 * 100);
    var tier2Cents = Math.round(tier2 * 100);

    // Apply currency rate to match what the theme's own bar would do.
    var rate =
      (window.Shopify && window.Shopify.currency && window.Shopify.currency.rate) || 1;
    tier1Cents = Math.round(tier1Cents * rate);
    tier2Cents = Math.round(tier2Cents * rate);

    var cartTotal = parseInt(bar.getAttribute('data-cart-total'), 10) || 0;

    var progressPct, tierLabel, remainderCents;

    if (cartTotal >= tier2Cents) {
      // Tier 2 unlocked — everything is free next business day.
      tierLabel = 'unlocked_tier2';
      progressPct = 100;
      setMessageText(messageEl, 'FREE next business day delivery unlocked!');
      bar.setAttribute(OVER_THRESHOLD_ATTR, 'true');
    } else if (cartTotal >= tier1Cents) {
      // Tier 1 unlocked, working toward tier 2. Progress bar resets and
      // fills across the (tier1, tier2) range.
      tierLabel = 'unlocked_tier1';
      remainderCents = tier2Cents - cartTotal;
      var tier2Range = tier2Cents - tier1Cents;
      progressPct = tier2Range > 0 ? ((cartTotal - tier1Cents) / tier2Range) * 100 : 100;
      setMessageText(
        messageEl,
        'FREE shipping unlocked! {{AMOUNT}} more for FREE next business day delivery',
        formatMoney(remainderCents)
      );
      bar.setAttribute(OVER_THRESHOLD_ATTR, 'false');
    } else {
      // Below tier 1 — standard "away from free shipping" message.
      tierLabel = 'below_tier1';
      remainderCents = tier1Cents - cartTotal;
      progressPct = tier1Cents > 0 ? (cartTotal / tier1Cents) * 100 : 0;
      setMessageText(
        messageEl,
        "You're {{AMOUNT}} away from FREE shipping",
        formatMoney(remainderCents)
      );
      bar.setAttribute(OVER_THRESHOLD_ATTR, 'false');
    }

    progressEl.style.setProperty('--progress-width', progressPct + '%');
    bar.setAttribute('data-is-loaded', 'true');
    bar.setAttribute('data-tier-state', tierLabel);

    // Tick of the event loop before re-enabling mutation-observer reactions.
    setTimeout(function () {
      updating = false;
    }, 30);
  }

  function renderAll() {
    var bars = document.querySelectorAll(SELECTOR);
    for (var i = 0; i < bars.length; i++) render(bars[i]);
  }

  // After cart mutations, the drawer re-renders its section HTML (via the
  // theme's refreshQuickCart). That re-render includes a NEW bar element with
  // the updated data-cart-total from the server. We need to re-render it to
  // replace the theme's stale static rendering.
  //
  // We watch the document for subtree changes and any time a new bar appears
  // or an existing bar's data-cart-total changes, we re-render.
  var observer = new MutationObserver(function (mutations) {
    if (updating) return;
    var needsRender = false;
    for (var i = 0; i < mutations.length; i++) {
      var m = mutations[i];
      if (m.type === 'attributes' && m.attributeName === 'data-cart-total') {
        needsRender = true;
        break;
      }
      if (m.type === 'childList') {
        for (var j = 0; j < m.addedNodes.length; j++) {
          var n = m.addedNodes[j];
          if (n.nodeType !== 1) continue;
          if (n.matches && n.matches(SELECTOR)) { needsRender = true; break; }
          if (n.querySelector && n.querySelector(SELECTOR)) { needsRender = true; break; }
        }
      }
      if (needsRender) break;
    }
    if (needsRender) renderAll();
  });

  function startObserver() {
    if (!document.body) return;
    observer.observe(document.body, {
      childList: true,
      subtree: true,
      attributes: true,
      attributeFilter: ['data-cart-total'],
    });
  }

  // Also poll cart.js periodically and update data-cart-total. This is the
  // belt-and-braces catch for cases where the drawer doesn't re-render (e.g.
  // the shipping bar lives outside the drawer on cart.liquid page).
  function refreshFromCart() {
    fetch('/cart.js', { credentials: 'same-origin' })
      .then(function (r) { return r.ok ? r.json() : null; })
      .then(function (cart) {
        if (!cart) return;
        var bars = document.querySelectorAll(SELECTOR);
        for (var i = 0; i < bars.length; i++) {
          var currentTotal = parseInt(bars[i].getAttribute('data-cart-total'), 10);
          if (currentTotal !== cart.total_price) {
            bars[i].setAttribute('data-cart-total', String(cart.total_price));
            render(bars[i]);
          }
        }
      })
      .catch(function () {});
  }

  // Hook into theme cart events (DOM-level CustomEvents dispatched by theme.js)
  // so we react immediately after a successful add/remove.
  document.addEventListener('cart:item-added', function () {
    setTimeout(refreshFromCart, 400);
  });
  document.addEventListener('cart:updated', function () {
    setTimeout(refreshFromCart, 400);
  });

  // Also intercept fetch calls to /cart/* endpoints — catches ANY code path
  // that mutates the cart, not just the theme's own flows. This is important
  // because the theme's cart:item-added event is only fired from the PDP form
  // handler; direct /cart/add.js calls (including ones from our own scripts)
  // wouldn't trigger a bar refresh otherwise.
  var _twotierOrigFetch = window.fetch;
  if (_twotierOrigFetch && !_twotierOrigFetch.__two_tier_patched) {
    window.fetch = function (url, opts) {
      var result = _twotierOrigFetch.apply(this, arguments);
      var urlStr = typeof url === 'string' ? url : (url && url.url) || '';
      if (/\/cart\/(add|change|update|clear)\.js/.test(urlStr)) {
        result.then(function (r) {
          if (r && (r.ok || r.status === 200)) {
            setTimeout(refreshFromCart, 300);
          }
        }).catch(function () {});
      }
      return result;
    };
    window.fetch.__two_tier_patched = true;
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () {
      renderAll();
      startObserver();
    });
  } else {
    renderAll();
    startObserver();
  }
})();
